/* SWSkinn Skin Analysis
   Flow: intro → camera → quality gate → adaptive questions → safety → scores → recommendation → WhatsApp handoff.

   Honesty notes (read before changing):
   - Scores are skin-wellbeing indicators, NOT diagnoses. Copy must never name conditions.
   - Image signals here are simple on-device measurements (shine, texture, tone evenness, redness variation).
     They carry at most ~25% of each score. Set config.analyseEndpoint to a validated vision model to replace them.
   - We never compute or infer age, sex, ethnicity, skin colour category or any other personal attribute.
   - The photo never leaves the device unless analyseEndpoint is configured (privacy copy switches automatically). */
(function () {
  'use strict';
  var CAT = window.SW_CATALOGUE, CFG = CAT.config;
  var track = function (e, p) { window.SW && window.SW.track && window.SW.track(e, p); };
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var qs = new URLSearchParams(location.search);

  var state = {
    sessionId: (window.SW && window.SW.sessionId) || ('sw-' + Date.now().toString(36)),
    image: null,          // { signals, quality } — never the pixels
    answers: {},
    usingProducts: (qs.get('using') || '').split(',').filter(function (x) { return CAT.products[x]; }),
    existingCustomer: qs.get('ctx') === 'existing'
  };

  // ---------- screens ----------
  function show(name) {
    $$('.an__screen').forEach(function (s) { s.classList.toggle('active', s.dataset.screen === name); });
    window.scrollTo(0, 0);
  }

  // ---------- intro ----------
  if (CFG.analyseEndpoint) {
    $('#privacy-copy').textContent = 'Your scan is sent securely to our trusted AI analysis provider, only to read visible skin features. SWSkinn does not store your photo, it is not used to train AI, and it is never used to infer anything about who you are. Only your scores and answers are used for your recommendation.';
  }
  try {
    var last = JSON.parse(localStorage.getItem('sw_last_analysis') || 'null');
    if (last && last.scores) {
      var el = $('#last-analysis');
      el.hidden = false;
      el.innerHTML = 'Welcome back. Your last analysis on ' + new Date(last.at).toLocaleDateString() +
        ': Clarity ' + last.scores.ACNE + ' · Barrier ' + last.scores.BARRIER + ' · Vitality ' + last.scores.VITALITY +
        '. Reassessing lets you see how your skin has changed.';
      state.previous = last;
    }
  } catch (e) {}

  $('#start-scan').addEventListener('click', function () {
    track(state.previous ? 'reassessment_start' : 'analysis_start', { mode: 'scan' });
    openCamera();
  });
  $('#skip-scan').addEventListener('click', function () {
    track(state.previous ? 'reassessment_start' : 'analysis_start', { mode: 'questions_only' });
    startQuestions();
  });

  // ---------- face detection (MediaPipe, lazy; graceful fallback) ----------
  var detector = null, detectorTried = false;
  function loadDetector() {
    if (detectorTried) return Promise.resolve(detector);
    detectorTried = true;
    var base = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
    return import(base + '/vision_bundle.mjs').then(function (v) {
      return v.FilesetResolver.forVisionTasks(base + '/wasm').then(function (fs) {
        return v.FaceDetector.createFromOptions(fs, {
          baseOptions: { modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite' },
          runningMode: 'IMAGE', minDetectionConfidence: 0.6
        });
      });
    }).then(function (d) { detector = d; return d; })
      .catch(function (err) { console.warn('Face detector unavailable', err); return null; });
  }
  function detectFaces(canvas) {
    if (!detector) return null; // unknown
    try {
      return detector.detect(canvas).detections.map(function (d) {
        var b = d.boundingBox;
        return { x: b.originX, y: b.originY, w: b.width, h: b.height, kp: (d.keypoints || []).map(function (k) { return { x: k.x * canvas.width, y: k.y * canvas.height }; }) };
      });
    } catch (e) { return null; }
  }

  // ---------- pixel measurements ----------
  function lumAt(d, i) { return 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]; }
  function regionStats(ctx, x, y, w, h) {
    x = Math.max(0, Math.round(x)); y = Math.max(0, Math.round(y));
    w = Math.max(4, Math.round(w)); h = Math.max(4, Math.round(h));
    var d = ctx.getImageData(x, y, w, h).data, n = w * h, sum = 0, sum2 = 0, clip = 0, red = [], shine = 0;
    var gray = new Float32Array(n);
    for (var p = 0, i = 0; p < n; p++, i += 4) {
      var L = lumAt(d, i); gray[p] = L; sum += L; sum2 += L * L;
      if (L > 245) clip++;
      var mx = Math.max(d[i], d[i + 1], d[i + 2]), mn = Math.min(d[i], d[i + 1], d[i + 2]);
      if (L > 200 && (mx - mn) / (mx || 1) < 0.18) shine++;          // bright + desaturated = specular
      red.push((d[i] - d[i + 1]) / (d[i] + d[i + 1] + d[i + 2] + 1)); // relative redness, tone-normalised
    }
    var mean = sum / n, sd = Math.sqrt(Math.max(0, sum2 / n - mean * mean));
    // Laplacian variance (sharpness / fine texture)
    var lap = 0, lap2 = 0, m = 0;
    for (var yy = 1; yy < h - 1; yy++) for (var xx = 1; xx < w - 1; xx++) {
      var k = yy * w + xx;
      var v = 4 * gray[k] - gray[k - 1] - gray[k + 1] - gray[k - w] - gray[k + w];
      lap += v; lap2 += v * v; m++;
    }
    var lapVar = m ? lap2 / m - Math.pow(lap / m, 2) : 0;
    var rm = red.reduce(function (a, b) { return a + b; }, 0) / red.length;
    var rsd = Math.sqrt(red.reduce(function (a, b) { return a + (b - rm) * (b - rm); }, 0) / red.length);
    return { mean: mean, sd: sd, clip: clip / n, shine: shine / n, lapVar: lapVar, redSd: rsd };
  }

  // Quality assessment → { ok, checks, issues[], faceBox }
  function assess(canvas, ctx, live) {
    var W = canvas.width, H = canvas.height;
    var faces = detectFaces(canvas);
    var box = null, issues = [], checks = { light: false, even: false, face: false, sharp: false };
    if (faces === null) {
      box = { x: W * 0.22, y: H * 0.18, w: W * 0.56, h: H * 0.6 }; // centre crop fallback (detector unavailable)
      checks.face = true;
    } else if (faces.length === 0) {
      issues.push(['No face found', 'Centre your face inside the oval, with your whole face visible.']);
    } else if (faces.length > 1) {
      issues.push(['More than one face', 'Make sure only you are in the frame.']);
    } else {
      box = faces[0];
      var cx = (box.x + box.w / 2) / W, cy = (box.y + box.h / 2) / H, fw = box.w / W;
      if (Math.abs(cx - 0.5) > 0.22 || Math.abs(cy - 0.47) > 0.26) issues.push(['Face not centred', 'Move so your face sits inside the oval.']);
      else if (fw < 0.18) issues.push(['Too far away', 'Bring the camera a little closer.']);
      else if (fw > 0.92) issues.push(['Too close', 'Move the camera a little further away.']);
      else checks.face = true;
      if (box.kp && box.kp.length >= 2) {
        var ang = Math.abs(Math.atan2(box.kp[1].y - box.kp[0].y, box.kp[1].x - box.kp[0].x) * 180 / Math.PI);
        if (ang > 90) ang = 180 - ang;
        if (ang > 20) { checks.face = false; issues.push(['Head tilted', 'Keep your head straight and look at the camera.']); }
      }
    }
    if (!box) return { ok: false, checks: checks, issues: issues };

    var all = regionStats(ctx, box.x, box.y, box.w, box.h);
    var left = regionStats(ctx, box.x, box.y + box.h * 0.3, box.w / 2, box.h * 0.5);
    var right = regionStats(ctx, box.x + box.w / 2, box.y + box.h * 0.3, box.w / 2, box.h * 0.5);

    if (all.mean < 38) issues.push(['Too dark', 'Move towards a window or turn on a soft light in front of you.']);
    else if (all.mean > 240 || all.clip > 0.3) issues.push(['Too bright or glare', 'Avoid direct sun or flash; use softer light.']);
    else checks.light = true;

    var diff = Math.abs(left.mean - right.mean) / Math.max(1, (left.mean + right.mean) / 2);
    if (diff > 0.55) issues.push(['Uneven light', 'Light is coming from one side. Face the light source directly.']);
    else checks.even = true;

    var sharpMin = live ? 8 : 10;
    if (all.lapVar < sharpMin) issues.push(['Blurry', 'Hold the phone steady and wipe the lens.']);
    else checks.sharp = true;

    if (!live && faces && box.kp && box.kp.length >= 2) {
      // Sunglasses: eye regions much darker than cheeks
      var eyeR = box.w * 0.12, cheek = regionStats(ctx, box.x + box.w * 0.2, box.y + box.h * 0.6, box.w * 0.2, box.h * 0.15);
      var e1 = regionStats(ctx, box.kp[0].x - eyeR, box.kp[0].y - eyeR / 2, eyeR * 2, eyeR);
      var e2 = regionStats(ctx, box.kp[1].x - eyeR, box.kp[1].y - eyeR / 2, eyeR * 2, eyeR);
      if ((e1.mean + e2.mean) / 2 < cheek.mean * 0.38) issues.push(['Eyes covered', 'Please remove sunglasses or anything covering your eyes.']);
      // Beauty filter: skin texture almost absent while the eyes are sharp
      if (cheek.lapVar < 3 && (e1.lapVar + e2.lapVar) / 2 > 40) issues.push(['Looks filtered or smoothed', 'Turn off beauty mode or filters so we can see your natural skin texture.']);
    }
    var HARD = ['No face found', 'More than one face', 'Too dark', 'Blurry'];
    var blocking = (!live && CFG.analyseEndpoint) ? issues.filter(function (i) { return HARD.indexOf(i[0]) > -1; }) : issues;
    return { ok: blocking.length === 0, checks: checks, issues: blocking, faceBox: box, detector: !!faces };
  }

  // Image signals (0 = no visible concern, 1 = strong visible cue). Relative & tone-normalised.
  function imageSignals(ctx, box) {
    var reg = function (fx, fy, fw, fh) { return regionStats(ctx, box.x + box.w * fx, box.y + box.h * fy, box.w * fw, box.h * fh); };
    var forehead = reg(0.25, 0.08, 0.5, 0.18), lc = reg(0.12, 0.52, 0.26, 0.2), rc = reg(0.62, 0.52, 0.26, 0.2), nose = reg(0.4, 0.45, 0.2, 0.22);
    var clamp = function (v) { return Math.max(0, Math.min(1, v)); };
    var cheeks = [lc, rc];
    var shine = clamp(((forehead.shine + nose.shine) / 2) / 0.06);
    var texture = clamp((((lc.lapVar + rc.lapVar) / 2) - 15) / 120);
    var tone = clamp((cheeks.concat([forehead]).reduce(function (a, r) { return a + r.sd / Math.max(1, r.mean); }, 0) / 3 - 0.05) / 0.12);
    var redness = clamp((((lc.redSd + rc.redSd) / 2) - 0.012) / 0.03);
    return { shine: +shine.toFixed(2), texture: +texture.toFixed(2), tone: +tone.toFixed(2), redness: +redness.toFixed(2), source: 'on-device' };
  }

  // ---------- camera ----------
  var stream = null, loopId = null, work = document.createElement('canvas'), wctx = work.getContext('2d', { willReadFrequently: true });
  var cam = $('#cam'), video = $('#video'), hint = $('#cam-hint'), shutter = $('#shutter');

  function openCamera() {
    shots = []; setTimeout(poseHint, 0);
    cam.classList.add('active');
    document.body.style.overflow = 'hidden';
    hint.textContent = 'Starting camera…';
    loadDetector();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      hint.textContent = 'Camera not supported here. Upload a photo instead.';
      track('camera_failure', { reason: 'unsupported' });
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1706 } }, audio: false })
      .then(function (s) {
        stream = s; video.srcObject = s;
        track('camera_open');
        video.onloadedmetadata = function () { video.play(); liveLoop(); };
      })
      .catch(function (err) {
        var denied = err && (err.name === 'NotAllowedError' || err.name === 'SecurityError');
        track(denied ? 'camera_permission_denied' : 'camera_failure', { reason: err && err.name });
        hint.textContent = denied
          ? 'Camera access was blocked. Allow it in your browser settings, or upload a photo below.'
          : 'We couldn\'t start the camera. Upload a photo below, or close and continue with questions.';
      });
  }
  function closeCamera() {
    if (loopId) clearTimeout(loopId);
    if (stream) stream.getTracks().forEach(function (t) { t.stop(); });
    stream = null;
    cam.classList.remove('active');
    document.body.style.overflow = '';
  }
  $('#cam-close').addEventListener('click', function () { closeCamera(); show('intro'); });

  function drawFrame(src, sw, sh, maxW) {
    var scale = Math.min(1, maxW / sw);
    work.width = Math.round(sw * scale); work.height = Math.round(sh * scale);
    wctx.drawImage(src, 0, 0, work.width, work.height);
  }
  var HINTS = { light: 'Find softer, brighter light', even: 'Face the light directly', face: 'Centre your face in the oval', sharp: 'Hold still' };
  function liveLoop() {
    if (!stream) return;
    if (video.videoWidth) {
      drawFrame(video, video.videoWidth, video.videoHeight, 360);
      var r = assess(work, wctx, true);
      $$('.cam__checks span').forEach(function (s) { s.classList.toggle('ok', !!r.checks[s.dataset.check]); });
      var side = shots.length > 0; // side poses: face is turned, so only light and focus must pass
      var allOk = r.checks.light && r.checks.sharp && (side || r.checks.face);
      shutter.disabled = !allOk;
      $('#cam-mask').classList.toggle('ready', allOk);
      var firstBad = ['face', 'light', 'even', 'sharp'].filter(function (k) { return !r.checks[k]; })[0];
      hint.textContent = allOk ? (side ? 'Good — capture this side' : 'Perfect — take the photo') : HINTS[side && (firstBad === 'face' || firstBad === 'even') ? 'light' : firstBad];
    }
    loopId = setTimeout(liveLoop, 280);
  }
  // Three angles: front first (used for the on-device quality gate), then each side for cheeks and jawline.
  var POSES = [
    { id: 'front', hint: 'Look straight at the camera' },
    { id: 'left', hint: 'Now turn your head slightly to the left' },
    { id: 'right', hint: 'Now turn your head slightly to the right' }
  ];
  var shots = [];
  function snap() { var c = document.createElement('canvas'), sc = Math.min(1, 900 / video.videoWidth);
    c.width = Math.round(video.videoWidth * sc); c.height = Math.round(video.videoHeight * sc);
    c.getContext('2d').drawImage(video, 0, 0, c.width, c.height); return c; }
  function poseHint() { var p = POSES[shots.length]; $('#cam-pose').textContent = (shots.length + 1) + ' of 3 · ' + p.hint; }
  shutter.addEventListener('click', function () {
    if (shots.length === 0) drawFrame(video, video.videoWidth, video.videoHeight, 900);
    shots.push(snap());
    track('camera_pose', { pose: POSES[shots.length - 1].id });
    if (shots.length < POSES.length) { poseHint(); return; }
    closeCamera();
    evaluateCapture('camera');
  });
  $('#upload').addEventListener('change', function (e) {
    var f = e.target.files && e.target.files[0];
    if (!f) return;
    var img = new Image();
    img.onload = function () {
      drawFrame(img, img.naturalWidth, img.naturalHeight, 900);
      shots = [];
      URL.revokeObjectURL(img.src);
      closeCamera();
      evaluateCapture('upload');
    };
    img.src = URL.createObjectURL(f);
    e.target.value = '';
  });

  function evaluateCapture(source) {
    show('review');
    var rc = $('#review-canvas'); rc.width = work.width; rc.height = work.height;
    rc.getContext('2d').drawImage(work, 0, 0);
    $('#review-issues').innerHTML = ''; $('#review-actions').innerHTML = '';
    $('#review-title').textContent = 'One moment…';
    loadDetector().then(function () {
      var r = assess(work, wctx, false);
      if (!r.ok) {
        track('image_rejected', { source: source, reasons: r.issues.map(function (i) { return i[0]; }).join('|') });
        $('#review-eyebrow').textContent = 'Let\'s retake that';
        $('#review-title').textContent = 'We can\'t read this photo reliably.';
        $('#review-copy').textContent = 'Rather than guess, we\'d like a clearer photo. Here\'s what to adjust:';
        $('#review-issues').innerHTML = r.issues.map(function (i) { return '<li><b>' + i[0] + '</b>' + i[1] + '</li>'; }).join('');
        actions([['Retake photo', function () { show('intro'); openCamera(); }, 'btn'],
                 ['Continue with questions only', startQuestions, 'btn btn--ghost']]);
        return;
      }
      track('camera_complete', { source: source, detector: r.detector });
      var sig = imageSignals(wctx, r.faceBox);
      state.image = { signals: sig, detector: r.detector };
      if (CFG.analyseEndpoint) {
        $('#review-eyebrow').textContent = 'Photo captured';
        $('#review-title').textContent = 'Reading your skin…';
        $('#review-copy').textContent = 'This takes a few seconds.';
      }
      maybeRemoteAnalyse().then(function (remote) {
        wipePixels();
        if (remote && remote.rejected) {
          track('image_rejected', { source: source, reasons: 'model:' + remote.rejected });
          $('#review-eyebrow').textContent = 'Let\'s retake that';
          $('#review-title').textContent = 'We can\'t read this photo reliably.';
          $('#review-copy').textContent = 'Rather than guess, we\'d like a clearer photo.';
          $('#review-issues').innerHTML = '<li><b>' + (MODEL_ISSUES[remote.rejected] || MODEL_ISSUES.other)[0] + '</b>' + (MODEL_ISSUES[remote.rejected] || MODEL_ISSUES.other)[1] + '</li>';
          actions([['Retake photo', function () { show('intro'); openCamera(); }, 'btn'],
                   ['Continue with questions only', function () { state.image = null; startQuestions(); }, 'btn btn--ghost']]);
          return;
        }
        $('#review-eyebrow').textContent = 'Photo accepted';
        $('#review-title').textContent = 'Clear and well lit.';
        $('#review-copy').textContent = 'Now six short questions. Only the ones that change your recommendation.';
        actions([['Continue', startQuestions, 'btn'], ['Retake', function () { show('intro'); openCamera(); }, 'btn btn--ghost']]);
      });
    });
  }
  var MODEL_ISSUES = {
    no_face: ['No face found', 'Centre your face inside the oval, with your whole face visible.'],
    multiple_faces: ['More than one face', 'Make sure only you are in the frame.'],
    too_dark: ['Too dark', 'Move towards a window or turn on a soft light in front of you.'],
    too_bright: ['Too bright or glare', 'Avoid direct sun or flash; use softer light.'],
    blurry: ['Blurry', 'Hold the phone steady and wipe the lens.'],
    face_covered: ['Face partly covered', 'Move hair away and remove sunglasses, masks or hands.'],
    filtered_or_edited: ['Looks filtered or edited', 'Turn off beauty mode or filters so we can see your natural skin.'],
    makeup_heavy: ['Heavy makeup', 'For the most accurate reading, scan with bare or lightly made-up skin.'],
    other: ['Photo not clear enough', 'Try again in good, even light, facing the camera.']
  };
  // Sends the captured frame (JPEG, ~900px) to the analysis service. Resolves to
  // { rejected: issue } when the service judges the photo unusable, otherwise null.
  // On any failure the on-device measurements are kept, so the flow never breaks.
  function maybeRemoteAnalyse() {
    if (!CFG.analyseEndpoint) return Promise.resolve(null);
    var imgs = (shots.length ? shots : [work]).map(function (c) { return c.toDataURL('image/jpeg', 0.85).split(',')[1]; });
    var ctrl = window.AbortController ? new AbortController() : null;
    var timer = ctrl && setTimeout(function () { ctrl.abort(); }, 30000);
    return fetch(CFG.analyseEndpoint, { method: 'POST', signal: ctrl && ctrl.signal,
        headers: { 'content-type': 'application/json', 'x-sw-session': state.sessionId }, body: JSON.stringify({ images: imgs, views: shots.length ? ['front', 'left', 'right'] : ['front'] }) })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) { track('analysis_model_failed', { reason: 'http' }); return null; }
        if (j.quality && j.quality.usable === false) return { rejected: j.quality.issue || 'other' };
        if (j.signals) {
          state.image.signals = Object.assign({}, j.signals, { source: 'model', confidence: j.confidence });
          track('analysis_model_ok', { confidence: j.confidence });
        }
        return null;
      })
      .catch(function (e) { track('analysis_model_failed', { reason: String(e && e.name) }); return null; })
      .then(function (v) { if (timer) clearTimeout(timer); return v; });
  }
  function wipePixels() { wctx.clearRect(0, 0, work.width, work.height); work.width = work.height = 1; shots.forEach(function (c) { c.width = c.height = 1; }); shots = []; }
  function actions(list) {
    var box = $('#review-actions'); box.innerHTML = '';
    list.forEach(function (a) { var b = document.createElement('button'); b.type = 'button'; b.className = a[2]; b.textContent = a[0]; b.onclick = a[1]; box.appendChild(b); });
  }

  // ---------- questionnaire (adaptive) ----------
  var QUESTIONS = [
    { id: 'concern', multi: true, title: 'What would you like to improve?', help: 'Select all that apply. Choose the most important first.', options: [
      ['acne', 'Breakouts, oiliness or visible pores'], ['barrier', 'Dryness, tightness or sensitivity'],
      ['tone', 'Dullness or uneven-looking tone'], ['ageing', 'Fine lines or loss of firmness'], ['unsure', 'Not sure — I want healthy skin']] },
    { id: 'skinType', title: 'How would you describe your skin type?', help: 'Think about how it feels by the middle of the day.', options: [
      ['oily', 'Oily', 'Shiny all over'], ['combination', 'Combination', 'Oily T-zone, normal or dry elsewhere'],
      ['normal', 'Balanced'], ['dry', 'Dry', 'Often feels tight or rough'], ['unsure', 'Not sure']] },
    { id: 'breakouts', title: 'How often do you get breakouts?', when: function (a) { return (a.concern || []).indexOf('acne') > -1 || a.skinType === 'oily' || a.skinType === 'combination'; }, options: [
      ['rare', 'Rarely or never'], ['monthly', 'A few times a month'], ['weekly', 'Most weeks'], ['constant', 'Almost all the time']] },
    { id: 'feeling', title: 'Most days, your skin feels…', options: [
      ['comfortable', 'Comfortable'], ['tight', 'Tight or dry after cleansing'], ['reactive', 'Easily stings, itches or turns red'], ['oily', 'Oily or shiny by midday']] },
    { id: 'actives', multi: true, title: 'What are you using on your skin now?', help: 'Select all that apply. This keeps your routine safe.', options: [
      ['none', 'Nothing active — cleanser and moisturiser only'], ['retinoid', 'Retinol or another retinoid (non-prescription)'],
      ['acids', 'Exfoliating acids (AHA, BHA, salicylic, glycolic)'], ['bpo', 'Benzoyl peroxide'], ['vitc', 'Vitamin C serum'],
      ['prescription', 'A treatment prescribed by a doctor', 'Creams, gels or tablets']] },
    { id: 'goal', title: 'What kind of routine suits you?', options: [
      ['minimal', 'The fewest products possible'], ['alongside', 'One product to add to my current routine'],
      ['complete', 'A complete routine I can follow'], ['agewell', 'A routine to help me age well over time']] },
    { id: 'safety', multi: true, title: 'Right now, are you experiencing any of these?', help: 'We ask so we can point you to the right care.', options: [
      ['none', 'None of these'], ['pain', 'Painful swelling or deep, painful lumps'], ['blister', 'Blistering, oozing or crusting'],
      ['worsening', 'Skin getting rapidly worse'], ['eyes', 'Symptoms around or in the eyes'], ['unwell', 'Fever or feeling unwell with skin symptoms'],
      ['reaction', 'A reaction to a product (burning, swelling, hives)']] }
  ];
  var qIndex = 0, stepCount = 0;
  function activeQuestions() { return QUESTIONS.filter(function (q) { return !q.when || q.when(state.answers); }); }

  function startQuestions() { qIndex = 0; stepCount = 0; show('questions'); renderQ(); }

  function renderQ() {
    var list = activeQuestions(), q = list[qIndex];
    var root = $('#q-root'), val = state.answers[q.id];
    var sel = function (k) { return q.multi ? (val || []).indexOf(k) > -1 : val === k; };
    root.innerHTML =
      '<div class="progress"><span style="width:' + Math.round((qIndex / list.length) * 100) + '%"></span></div>' +
      '<div class="q__meta"><span>Question ' + (qIndex + 1) + ' of ' + list.length + '</span>' +
      (qIndex > 0 ? '<button class="q__back" type="button">← Back</button>' : '<span></span>') + '</div>' +
      '<h2 id="q-title">' + q.title + '</h2>' + (q.help ? '<p class="q__help">' + q.help + '</p>' : '<div style="height:20px"></div>') +
      '<div class="opts" role="group" aria-labelledby="q-title">' + q.options.map(function (o) {
        return '<button type="button" class="opt" data-v="' + o[0] + '" aria-pressed="' + sel(o[0]) + '">' +
          (q.multi ? '<span class="opt__box" aria-hidden="true"></span>' : '') +
          '<span>' + o[1] + (o[2] ? '<small>' + o[2] + '</small>' : '') + '</span></button>';
      }).join('') + '</div>' +
      (q.multi ? '<button class="btn q__next" type="button"' + ((val || []).length ? '' : ' disabled') + '>Continue <span class="arrow"></span></button>' : '');

    var back = $('.q__back', root);
    if (back) back.onclick = function () { qIndex--; renderQ(); };
    $$('.opt', root).forEach(function (b) {
      b.onclick = function () {
        var v = b.dataset.v;
        if (!q.multi) { state.answers[q.id] = v; next(q); return; }
        var arr = (state.answers[q.id] || []).slice();
        if (v === 'none' || v === 'unsure') arr = arr.indexOf(v) > -1 ? [] : [v];
        else { arr = arr.filter(function (x) { return x !== 'none' && x !== 'unsure'; }); var i = arr.indexOf(v); i > -1 ? arr.splice(i, 1) : arr.push(v); }
        state.answers[q.id] = arr;
        renderQ();
      };
    });
    var nx = $('.q__next', root);
    if (nx) nx.onclick = function () { next(q); };
    var t = $('#q-title'); t && t.setAttribute('tabindex', '-1'); t && t.focus({ preventScroll: true });
  }
  function next(q) {
    track('questionnaire_step', { question: q.id });
    var list = activeQuestions();
    // Drop answers for questions no longer shown (e.g. breakouts after concern changed)
    QUESTIONS.forEach(function (x) { if (list.indexOf(x) === -1) delete state.answers[x.id]; });
    if (qIndex < list.length - 1) { qIndex++; renderQ(); return; }
    track('questionnaire_complete', { with_image: !!state.image });
    var red = (state.answers.safety || []).filter(function (x) { return x !== 'none'; });
    if (red.length) return escalate(red);
    show('computing');
    setTimeout(function () { renderResults(compute()); }, 1300);
  }

  function escalate(flags) {
    track('safety_escalation', { flags: flags.join('|') });
    var labels = QUESTIONS[QUESTIONS.length - 1].options.reduce(function (m, o) { m[o[0]] = o[1]; return m; }, {});
    $('#escalate-list').innerHTML = flags.map(function (f) { return '<li>' + labels[f] + '</li>'; }).join('');
    $('#escalate-product').hidden = flags.indexOf('reaction') === -1;
    show('escalate');
  }

  // ---------- scoring ----------
  function compute() {
    var a = state.answers, s = state.image && state.image.signals, W = s ? 1 : 0;
    var C = a.concern || [], has = function (c) { return C.indexOf(c) > -1; };
    var M = s && s.source === 'model' ? (s.confidence === 'low' ? 0.5 : 1) : 0; // model readings, down-weighted when unsure
    var acts = a.actives || [];
    var clamp = function (v) { return Math.round(Math.max(20, Math.min(95, v))); };

    var acne = 86;
    if (has('acne')) acne -= 18;
    acne -= { oily: 8, combination: 5 }[a.skinType] || 0;
    acne -= { monthly: 10, weekly: 22, constant: 32 }[a.breakouts] || 0;
    if (a.feeling === 'oily') acne -= 6;
    if (M) acne -= M * (14 * s.blemishes + 6 * s.shine + 5 * s.texture);
    else if (W) acne -= 9 * s.shine + 6 * s.texture + 5 * s.redness;

    var barrier = 86;
    if (has('barrier')) barrier -= 18;
    if (a.skinType === 'dry') barrier -= 10;
    barrier -= { tight: 14, reactive: 20 }[a.feeling] || 0;
    barrier -= Math.min(14, (acts.indexOf('retinoid') > -1 ? 5 : 0) + (acts.indexOf('acids') > -1 ? 5 : 0) + (acts.indexOf('bpo') > -1 ? 6 : 0) + (acts.indexOf('prescription') > -1 ? 6 : 0));
    if (M) barrier -= M * (8 * s.redness + 8 * s.dryness);
    else if (W) barrier -= 10 * s.redness;

    var vitality = 86;
    vitality -= Math.max(has('tone') ? 18 : 0, has('ageing') ? 20 : 0);
    if (a.goal === 'agewell') vitality -= 6;
    if (M) vitality -= M * (8 * s.tone + 6 * s.dullness + 8 * s.lines);
    else if (W) vitality -= 12 * s.tone + 4 * s.texture;

    var scores = { ACNE: clamp(acne), BARRIER: clamp(barrier), VITALITY: clamp(vitality) };
    var concernDim = { acne: 'ACNE', barrier: 'BARRIER', tone: 'VITALITY', ageing: 'VITALITY' }[C[0]];
    var order = Object.keys(scores).sort(function (x, y) { return scores[x] - scores[y] || (x === concernDim ? -1 : y === concernDim ? 1 : 0); });
    var primary = order[0];
    var secondary = scores[order[1]] < 70 ? order[1] : null;
    return recommend({ scores: scores, primary: primary, secondary: secondary, order: order });
  }

  // ---------- recommendation engine ----------
  var DIM_PRODUCT = { ACNE: 'primask', BARRIER: 'restore', VITALITY: 'vitality' };
  var DIM_LABEL = { ACNE: 'Clarity & Control', BARRIER: 'Repair & Resilience', VITALITY: 'Radiance & Age Wellbeing' };

  function recommend(r) {
    var a = state.answers, acts = a.actives || [], notes = [];
    var S = r.scores, P = r.primary, X = r.secondary;
    var allLow = S.ACNE < 70 && S.BARRIER < 70 && S.VITALITY < 70;
    var routine = null, products;

    if (allLow) routine = (a.goal === 'agewell' || C.indexOf('ageing') > -1) ? 'age-well' : 'complete-skin-routine';
    else if (X) {
      var pair = [P, X].sort().join('+');
      routine = { 'ACNE+BARRIER': 'advanced-anti-acne-system', 'BARRIER+VITALITY': 'vitality-barrier' }[pair] || null;
    } else if (P === 'ACNE' && a.goal === 'complete') routine = 'advanced-anti-acne-system';
    else if (P === 'VITALITY' && a.goal === 'agewell' && S.BARRIER < 80) routine = 'vitality-barrier';

    products = routine ? CAT.routines[routine].products.slice() : [DIM_PRODUCT[P]].concat(X ? [DIM_PRODUCT[X]] : []);

    // Customer wants fewer products → keep the primary product only
    if (a.goal === 'minimal' || a.goal === 'alongside') {
      if (products.length > 1) notes.push('You asked for a minimal routine, so we\'ve started with the one product for your primary priority. ' + (X ? 'Your secondary priority can be added later.' : ''));
      products = [DIM_PRODUCT[P]]; routine = null;
    }

    // Safety modifiers
    if (acts.indexOf('prescription') > -1) {
      var dropped = products.filter(function (p) { return p !== 'restore'; });
      if (dropped.length) notes.push('Because you\'re using a prescribed treatment, we\'ve recommended only Restore, our gentle barrier moisturiser. Please check with your doctor before adding other active products, and keep following their advice.');
      products = ['restore']; routine = null;
    }
    if (products.indexOf('vitality') > -1 && acts.indexOf('retinoid') > -1)
      notes.push('Vitality contains retinol. Use it instead of your current retinoid, not as well as it.');
    if (products.indexOf('vitality') > -1 && (a.feeling === 'reactive' || S.BARRIER < 60))
      notes.push('Your barrier needs support first. Use Restore on its own for two weeks, then introduce Vitality two or three evenings a week.');
    if (products.indexOf('primask') > -1 && (acts.indexOf('acids') > -1 || acts.indexOf('bpo') > -1))
      notes.push('On days you use Primask, skip your exfoliating acid or benzoyl peroxide to avoid over-drying.');

    // Existing-customer context: don't re-sell what they already use
    var owned = products.filter(function (p) { return state.usingProducts.indexOf(p) > -1; });
    if (owned.length) notes.push('You already use ' + owned.map(function (p) { return CAT.products[p].name; }).join(' and ') + '. Keep going; it stays in your routine below.');

    r.products = products;
    r.routineId = routine && products.length === CAT.routines[routine].products.length ? routine : null;
    r.notes = notes;
    r.steps = buildSteps(products);
    r.explanation = explain(r);
    return r;
  }

  function buildSteps(products) {
    var has = function (p) { return products.indexOf(p) > -1; };
    var am = ['Cleanse gently with lukewarm water'], pm = ['Cleanse to remove sunscreen and the day'];
    if (has('restore')) am.push('Restore — 2–3 pumps, pat in until absorbed');
    else am.push('Your usual light moisturiser');
    am.push('Broad-spectrum sunscreen, SPF 30 or higher');
    if (has('vitality')) pm.push('Vitality — a small amount, avoiding the eye contour');
    if (has('restore')) pm.push('Restore — 2–3 pumps to finish');
    var weekly = has('primask') ? ['Primask on clean skin, as directed on the pack, two to three times a week' + (has('restore') ? ', followed by Restore' : '')] : [];
    return { am: am, pm: pm, weekly: weekly };
  }

  function explain(r) {
    var why = {
      ACNE: 'Your answers' + (state.image ? ' and scan' : '') + ' point to oil balance and congestion as the area where your skin needs the most support right now.',
      BARRIER: 'Your answers' + (state.image ? ' and scan' : '') + ' suggest your skin barrier is under some strain, so comfort and resilience come first.',
      VITALITY: 'Your answers' + (state.image ? ' and scan' : '') + ' suggest radiance and the visible signs of time are where your skin would benefit most.'
    };
    var t = why[r.primary];
    if (r.secondary) t += ' ' + DIM_LABEL[r.secondary] + ' is a secondary priority.';
    if (!r.secondary && Math.min(r.scores.ACNE, r.scores.BARRIER, r.scores.VITALITY) >= 75) t = 'Your skin looks well balanced across all three dimensions. Your primary priority is simply the area to maintain most carefully.';
    return t;
  }

  // ---------- results ----------
  function shopLink(p, campaign) {
    if (CFG.nativeCart) return '/products/' + CAT.products[p].shopifyHandle;
    return CFG.shopUrl + '/products/' + CAT.products[p].shopifyHandle + '?utm_source=swskinn.com&utm_medium=referral&utm_campaign=' + campaign + '&sw_session=' + encodeURIComponent(state.sessionId);
  }

  function renderResults(r) {
    state.result = r;
    state.analysedAt = new Date().toISOString();
    try { localStorage.setItem('sw_last_analysis', JSON.stringify({ at: state.analysedAt, scores: r.scores, primary: r.primary })); } catch (e) {}
    track('results_view', { acne: r.scores.ACNE, barrier: r.scores.BARRIER, vitality: r.scores.VITALITY, primary: r.primary, secondary: r.secondary, routine: r.routineId, products: r.products.join('|'), with_image: !!state.image });

    var dims = ['ACNE', 'BARRIER', 'VITALITY'];
    var prev = state.previous && state.previous.scores;
    var scoreHtml = dims.map(function (d) {
      var tag = d === r.primary ? 'Primary priority' : d === r.secondary ? 'Secondary priority' : (r.scores[d] >= 75 ? 'Doing well' : 'Keep an eye on');
      var delta = prev ? r.scores[d] - prev[d] : null;
      return '<div class="score' + (d === r.primary ? ' is-primary' : '') + '"><div class="score__key">' + d + '</div><div class="score__label">' + DIM_LABEL[d] + '</div>' +
        '<div class="score__num num">' + r.scores[d] + '<small>/ 100</small></div><div class="score__bar"><span data-w="' + r.scores[d] + '"></span></div>' +
        '<div class="score__tag">' + tag + (delta ? ' · ' + (delta > 0 ? '+' : '') + delta + ' since last time' : '') + '</div></div>';
    }).join('');

    var recHtml = r.products.map(function (p) {
      var P = CAT.products[p];
      return '<div class="rec" data-rec="' + p + '"><img src="' + P.image + '?width=240" alt="' + P.fullName + '" width="88" height="88" loading="lazy">' +
        '<div><h4>' + P.name + '</h4><p>' + P.descriptor + '</p><p class="stock" data-stock-rec="' + p + '" data-state="unknown">Checking availability</p>' +
        '<a class="textlink" style="margin-top:10px" data-track="shopify_click" data-source="results" data-shop-link="' + p + '" href="' + shopLink(p, 'analysis_results') + '">' + (CFG.nativeCart ? 'View product' : 'View on SWSkinnShop') + '</a></div></div>';
    }).join('');

    var list = function (arr) { return arr.map(function (x) { return '<li><div><span>' + x + '</span></div></li>'; }).join(''); };
    var routineName = r.routineId ? CAT.routines[r.routineId].name : (r.products.length > 1 ? 'Your routine' : 'Your first step');

    $('#results-root').innerHTML =
      '<div class="res__head"><span class="eyebrow">Your skin priorities</span><h1 style="font-size:clamp(40px,6vw,76px)">Here\'s what your skin <em>is telling us.</em></h1>' +
      (!state.image ? '<p class="small muted">Based on your answers only. Add a face scan next time for a fuller picture.</p>' : '') + '</div>' +
      '<div class="scores">' + scoreHtml + '</div>' +
      '<div class="priority"><div><span class="eyebrow">Primary skin priority</span><h2>' + DIM_LABEL[r.primary] + '</h2>' +
      (r.secondary ? '<p class="muted">Secondary: ' + DIM_LABEL[r.secondary] + '</p>' : '') + '</div>' +
      '<div><p class="lede" style="margin-bottom:12px">' + r.explanation + '</p>' +
      r.notes.map(function (n) { return '<p class="flag">' + n + '</p>'; }).join('') + '</div></div>' +

      '<section class="wa" id="wa"><div><span class="eyebrow" style="color:#b8c6f0">SWSkinn Personal Skin Advisor</span><h2>Get your routine on WhatsApp.</h2>' +
      '<p style="color:#cfd6ee;margin-top:14px">Personal AI skin guidance on WhatsApp, trained by skin experts. Your advisor receives your results, so you won\'t repeat anything. Ask questions, get reminders, and check in as your skin changes.</p></div>' +
      '<div><label class="consent"><input type="checkbox" id="wa-consent"><span>I agree that SWSkinn may contact me on WhatsApp about my skin analysis, routine and orders. My scores, priorities and recommended products will be shared with the SWSkinn advisor. My photo is never shared. I can reply STOP at any time. <a href="' + (CFG.nativeCart ? '/pages/privacy' : '/privacy/') + '" style="color:#fff">Privacy</a></span></label>' +
      '<button class="btn" id="wa-btn" type="button" disabled>Yes, send my routine on WhatsApp</button><p class="wa__status" id="wa-status" aria-live="polite"></p></div></section>' +

      '<div class="grid-2" style="gap:clamp(32px,5vw,72px)"><div><span class="eyebrow">Recommended</span><h2>' + routineName + '</h2>' +
      '<div class="rec-products">' + recHtml + '</div><div id="bundle-cta"></div></div>' +
      '<div><span class="eyebrow">How to use</span><h4 style="margin-top:8px">Morning</h4><ol class="steps">' + list(r.steps.am) + '</ol>' +
      '<h4 style="margin-top:28px">Evening</h4><ol class="steps">' + list(r.steps.pm) + '</ol>' +
      (r.steps.weekly.length ? '<h4 style="margin-top:28px">Weekly</h4><ol class="steps">' + list(r.steps.weekly) + '</ol>' : '') +
      '<p class="small muted" style="margin-top:20px">Patch test new products first. Results vary from person to person; give a routine six to eight weeks, then reassess.</p></div></div>' +

      '<p class="small muted" style="margin-top:56px;border-top:1px solid var(--rule);padding-top:20px">These scores are skin-wellbeing indicators to guide skincare choices, not a medical diagnosis. If your skin is painful, worsening quickly or you\'re concerned, please see a dermatologist or doctor.</p>';

    show('results');
    requestAnimationFrame(function () { $$('.score__bar span').forEach(function (s) { s.style.width = s.dataset.w + '%'; }); });

    var consent = $('#wa-consent'), waBtn = $('#wa-btn');
    consent.onchange = function () { waBtn.disabled = !consent.checked; };
    waBtn.onclick = handoff;
    applyAvailability(r);
  }

  function applyAvailability(r) {
    window.SW.shop.load().then(function (live) {
      var items = live && live.products, allIn = !!items;
      r.products.forEach(function (p) {
        var el = $('[data-stock-rec="' + p + '"]'), it = items && items[p];
        if (!it) { el.textContent = CFG.nativeCart ? 'See product page for availability' : 'See SWSkinnShop.com for availability'; allIn = false; return; }
        if (it.available) { el.textContent = 'Available'; el.dataset.state = 'in'; }
        else { el.textContent = 'Currently unavailable — ask your advisor on WhatsApp'; el.dataset.state = 'out'; allIn = false; }
      });
      r.availability = items ? r.products.reduce(function (m, p) { m[p] = !!(items[p] && items[p].available); return m; }, {}) : null;
      if (allIn && r.products.length > 1) {
        if (CFG.nativeCart) {
          // Inside the Shopify theme: add the routine to the store's own cart (same site, same cart).
          $('#bundle-cta').innerHTML = '<button type="button" class="btn" id="add-routine" data-track="add_routine_to_cart" data-source="results_routine" data-product="' + r.products.join('+') + '">Add routine to cart <span class="arrow"></span></button>';
          $('#add-routine').onclick = function () {
            var b = this; b.disabled = true;
            fetch('/cart/add.js', { method: 'POST', headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ items: r.products.map(function (p) { return { id: items[p].variantId, quantity: 1 }; }) }) })
              .then(function (res) { if (!res.ok) throw new Error(res.status); })
              .then(function () { return fetch('/cart/update.js', { method: 'POST', headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ attributes: { sw_session: state.sessionId, sw_routine: r.routineId || 'custom' } }) }); })
              .then(function () { location.href = '/cart'; })
              .catch(function () { b.disabled = false; b.firstChild.textContent = 'Please try again '; });
          };
        } else {
          // Separate-site mode: Shopify cart permalink, one native Shopify cart, no second checkout.
          var lines = r.products.map(function (p) { return items[p].variantId + ':1'; }).join(',');
          var url = CFG.shopUrl + '/cart/' + lines + '?attributes[sw_session]=' + encodeURIComponent(state.sessionId) +
            '&attributes[sw_routine]=' + encodeURIComponent(r.routineId || 'custom') + '&utm_source=swskinn.com&utm_medium=referral&utm_campaign=analysis_routine';
          $('#bundle-cta').innerHTML = '<a class="btn" data-track="shopify_click" data-source="results_routine" data-product="' + r.products.join('+') + '" href="' + url + '">Add routine to cart on SWSkinnShop <span class="arrow"></span></a>' +
            '<p class="small muted" style="margin-top:10px">Price and checkout on SWSkinnShop.com.</p>';
        }
      }
    });
  }

  // ---------- WhatsApp handoff ----------
  function handoff() {
    var r = state.result, btn = $('#wa-btn'), status = $('#wa-status');
    var optAt = new Date().toISOString();
    btn.disabled = true; status.textContent = 'Preparing your routine…';
    track('whatsapp_optin', { primary: r.primary });
    var payload = {
      session_id: state.sessionId,
      scores: { acne: r.scores.ACNE, barrier: r.scores.BARRIER, vitality: r.scores.VITALITY },
      primary_priority: r.primary, secondary_priority: r.secondary,
      recommended_products: r.products.map(function (p) { return { id: p, shopify_product_id: CAT.products[p].shopifyProductId, handle: CAT.products[p].shopifyHandle }; }),
      routine_id: r.routineId, notes: r.notes, answers: state.answers,
      image_used: !!state.image, image_signal_source: state.image ? state.image.signals.source : null,
      availability: r.availability || null, existing_customer: state.existingCustomer, using_products: state.usingProducts,
      referral_code: (window.SW && window.SW.referralCode) || null,
      analysed_at: state.analysedAt,
      whatsapp_opt_in: true, whatsapp_opt_in_at: optAt, consent_version: CFG.consentVersion
    };
    if (!CFG.handoffEndpoint) { track('whatsapp_handoff', { mode: 'message_only' }); openWhatsApp(null, null); return; }
    fetch(CFG.handoffEndpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (res) { if (!res.ok) throw new Error(res.status); return res.json(); })
      .then(function (j) { openWhatsApp(j.ref, j.whatsappNumber); track('whatsapp_handoff', { ref: j.ref }); })
      .catch(function (err) {
        track('whatsapp_handoff_failed', { reason: String(err) });
        openWhatsApp(null, null); // still carry context in the message so nothing needs repeating
      });
  }
  function openWhatsApp(ref, number) {
    var r = state.result, num = (number || CFG.whatsappNumber || '').replace(/\D/g, '');
    var status = $('#wa-status');
    var text = 'Hi SWSkinn, I\'ve just completed my skin analysis.' + (ref ? ' Ref: ' + ref + '.' : '') +
      ' Clarity ' + r.scores.ACNE + ', Barrier ' + r.scores.BARRIER + ', Vitality ' + r.scores.VITALITY +
      '. Primary priority: ' + DIM_LABEL[r.primary] + (r.secondary ? '; secondary: ' + DIM_LABEL[r.secondary] : '') +
      '. Recommended: ' + r.products.map(function (p) { return CAT.products[p].name; }).join(' + ') + '.' + (window.SW && window.SW.referralCode ? ' Code: ' + window.SW.referralCode + '.' : '') + ' Please send my routine.';
    if (!num) { status.textContent = 'WhatsApp number not configured yet (set whatsappNumber in catalogue.json).'; $('#wa-btn').disabled = false; return; }
    status.textContent = 'Opening WhatsApp…' + (ref ? ' Your reference is ' + ref + '.' : '');
    location.href = 'https://wa.me/' + num + '?text=' + encodeURIComponent(text);
  }
})();
